import { defineStore } from 'pinia'
import { ref } from 'vue'
import { publishExpiry, stopCoreSessionHandler, broadcastLogout, broadcastLogin } from "@/auth/coreSessionHandler";
import { useSuiteStore } from '@/stores/useSuiteStore';

const CORE_CSRF_KEY = 'core_csrf_token'
const TRICKSTER_KEY = 'core_trickster_user'

export interface TricksterTargetUser {
  uuid: string
  email: string
  firstName: string | null
  lastName: string | null
}

export const useAuthStore = defineStore('auth', () => {
  const suiteStore = useSuiteStore()
  const tricksterUser = ref<TricksterTargetUser | null>(null)

  // Stocké en localStorage pour partage entre onglets : front et back sont
  // sur des domaines racine différents, donc le cookie core_csrf n'est pas
  // lisible côté JS et sessionStorage est par-onglet.
  function getCoreCsrfToken(): string {
    return (typeof localStorage !== 'undefined' ? localStorage.getItem(CORE_CSRF_KEY) : null) ?? ''
  }

  function setCoreCsrfToken(token: string | null) {
    if (typeof localStorage !== 'undefined') {
      if (token) localStorage.setItem(CORE_CSRF_KEY, token)
      else localStorage.removeItem(CORE_CSRF_KEY)
    }
  }

  /** Restaure l'etat trickster au chargement (fallback avant que fetchMe ait repondu) */
  function initTricksterState() {
    if (typeof localStorage === 'undefined') return
    const raw = localStorage.getItem(TRICKSTER_KEY)
    if (raw) {
      try { tricksterUser.value = JSON.parse(raw) } catch { /* ignore */ }
    }
  }

  /** Synchronise l'etat trickster a partir de la reponse /me (autorite backend) */
  function syncTricksterFromMe(targetUser: TricksterTargetUser | null) {
    tricksterUser.value = targetUser
    if (typeof localStorage !== 'undefined') {
      if (targetUser) localStorage.setItem(TRICKSTER_KEY, JSON.stringify(targetUser))
      else localStorage.removeItem(TRICKSTER_KEY)
    }
  }

  /**
   * Perform a login on CORE API to establish a server-side session (cookies)
   * using the Azure AD access token and the user's OID.
   * Returns true if the call succeeded.
   */
  async function coreLogin(accessToken: string, oid?: string): Promise<boolean> {
    try {
      const res = await suiteStore.suiteFetch('/auth/user/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: accessToken, oid }),
        credentials: 'include',
      })

      if (!res || !res.ok) return false;

      const data = await res.json()
      if (data.expiresAt) publishExpiry(new Date(data.expiresAt).getTime())
      if (data.csrfToken) setCoreCsrfToken(data.csrfToken)

      // Notify other tabs (especially stale /login pages) that auth is restored.
      broadcastLogin()

      return true
    } catch (_) {
      return false
    }
  }

  /**
   * Perform a logout on CORE API to revoke the server-side session and clear cookies.
   * Returns true if the call succeeded.
   */
  /**
   * Local-only cleanup: stops the session-refresh scheduler and clears the
   * CSRF token. No API call, no cross-tab broadcast. Use for passive tabs
   * receiving a logout signal — the originating tab already hit the server.
   */
  function clearLocalSession() {
    stopCoreSessionHandler()
    setCoreCsrfToken(null)
  }

  async function coreLogout(opts?: { silent?: boolean }): Promise<boolean> {
    try {
      const res = await suiteStore.suiteFetch('/auth/user/logout', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': getCoreCsrfToken() },
      })

      stopCoreSessionHandler()
      setCoreCsrfToken(null)
      // Only broadcast for user-initiated logouts. Passive callers (reacting to
      // an Azure addUserUnloaded event triggered by another tab's localStorage
      // clear) must pass silent: true — otherwise every tab would re-broadcast
      // and cascade infinitely.
      if (!opts?.silent) broadcastLogout()

      if (!res || !res.ok) return false;

      return res.ok
    } catch (_) {
      return false
    }
  }

  /**
   * Perform a refresh on CORE API to extend the server-side session.
   * Returns true if the call succeeded.
   */
  async function coreRefresh(): Promise<boolean> {
    try {
      const res = await suiteStore.suiteFetch('/auth/user/refresh', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': getCoreCsrfToken() },
      })

      if (!res || !res.ok) return false;

      const data = await res.json()
      if (data.expiresAt) publishExpiry(new Date(data.expiresAt).getTime())
      if (data.csrfToken) setCoreCsrfToken(data.csrfToken)

      return true
    } catch (_) {
      return false
    }
  }

  async function coreLoginPassword(email: string, password: string): Promise<{ ok: boolean; error?: string }> {
    try {
      const res = await suiteStore.suiteFetch('/auth/user/login-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
        credentials: 'include',
      })

      if (!res) return { ok: false, error: 'network_error' }

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        return { ok: false, error: data.message ?? data.error ?? 'invalid_credentials' }
      }

      const data = await res.json()
      if (data.expiresAt) publishExpiry(new Date(data.expiresAt).getTime())
      if (data.csrfToken) setCoreCsrfToken(data.csrfToken)

      // Notify other tabs (especially stale /login pages) that auth is restored.
      broadcastLogin()

      return { ok: true }
    } catch (_) {
      return { ok: false, error: 'network_error' }
    }
  }

  /**
   * Appele apres que le backend a pose les cookies trickster.
   * Stocke l'identite du user impersone dans localStorage (partage entre onglets).
   */
  function startTrickster(data: { csrfToken: string; expiresAt: string; targetUser: TricksterTargetUser }) {
    setCoreCsrfToken(data.csrfToken)
    syncTricksterFromMe(data.targetUser)
    if (data.expiresAt) publishExpiry(new Date(data.expiresAt).getTime())
  }

  /**
   * Appelle le backend pour restaurer la session admin, puis nettoie l'etat local.
   */
  async function stopTrickster(): Promise<boolean> {
    const res = await suiteStore.suiteFetch('users/admin/trickster/restore', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })

    syncTricksterFromMe(null)

    if (res && typeof res !== 'boolean' && res.ok) {
      const data = await res.json()
      if (data.csrfToken) setCoreCsrfToken(data.csrfToken)
      if (data.expiresAt) publishExpiry(new Date(data.expiresAt).getTime())
      return true
    }
    return false
  }

  // Restaurer l'état trickster au chargement
  initTricksterState()

  return {
    tricksterUser,
    getCoreCsrfToken,
    coreLogin,
    coreLoginPassword,
    coreLogout,
    coreRefresh,
    clearLocalSession,
    setCoreCsrfTokenInternal: setCoreCsrfToken,
    startTrickster,
    stopTrickster,
    syncTricksterFromMe,
  }
})
